<template>
  <b-modal :id="modalId" size="xl" :hide-footer="true" :title="title">
    <div v-if="loading" class="text-center p-4">
      <b-spinner class="mr-2"></b-spinner> Cargando historial...
    </div>
    <template v-else>
      <p v-if="entries.length === 0" class="text-muted text-center my-4">
        No hay acciones registradas.
      </p>
      <div v-else class="audit-timeline">
        <div v-for="(entry, idx) in entries" :key="idx" class="audit-entry d-flex mb-3">
          <div class="audit-badge mr-3 text-center">
            <b-badge :variant="opVariant(entry.operation)" pill>{{ opLabel(entry.operation) }}</b-badge>
          </div>
          <div class="audit-body flex-grow-1">
            <div class="d-flex justify-content-between">
              <strong>{{ entityLabel(entry.entity) }}<span v-if="entry.name" class="text-primary"> — {{ entry.name }}</span></strong>
              <small class="text-muted">{{ formatDate(entry.timestamp) }}</small>
            </div>
            <div class="text-muted mb-1">
              <i class="fas fa-user"></i> {{ userName(entry.user_id) }}
            </div>
            <div v-if="entry.changes.length > 0">
              <b-table-simple small borderless class="mb-0">
                <b-tbody>
                  <b-tr v-for="(c, ci) in entry.changes" :key="ci">
                    <b-td class="pr-3"><strong>{{ fieldLabel(c.field) }}</strong></b-td>
                    <b-td class="text-danger"><s>{{ valueLabel(c.field, c.old) }}</s></b-td>
                    <b-td class="text-muted">→</b-td>
                    <b-td class="text-success">{{ valueLabel(c.field, c.new) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
            <div v-else-if="entry.operation === 'CREATE'" class="text-muted"><small>Se creó el registro.</small></div>
            <div v-else-if="entry.operation === 'DELETE'" class="text-muted"><small>Se eliminó el registro.</small></div>
            <div v-else class="text-muted"><small>Sin cambios de campos registrados.</small></div>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";

export default {
  name: "AuditTrailModal",
  props: {
    // Nombre de la tabla en el audit (ej. "purchase_order", "product").
    tableName: { type: String, required: true },
    recordId: { type: [Number, String], default: null },
    title: { type: String, default: "Historial de acciones" },
    // Id único del modal por si hay varios en la misma página.
    modalId: { type: String, default: "audit-trail-modal" },
  },
  data() {
    return {
      loading: false,
      entries: [],
      usersById: {},
    };
  },
  methods: {
    // Abrir el modal y cargar el historial del registro actual.
    async open() {
      if (this.recordId == null) return;
      this.$bvModal.show(this.modalId);
      this.loading = true;
      this.entries = [];
      try {
        if (Object.keys(this.usersById).length === 0) {
          try {
            const usersRes = await axios.get("/users");
            (usersRes.data || []).forEach(u => { this.usersById[u.id] = u.name || u.username; });
          } catch (e) { /* si falla, mostramos el id */ }
        }
        const res = await axios.get(`/audit/trail/${this.tableName}/${this.recordId}`);
        this.entries = this.build(res.data);
      } catch (err) {
        this.entries = [];
        this.$emit("error", err);
      } finally {
        this.loading = false;
      }
    },

    build(data) {
      if (!data) return [];
      const rows = [];
      const push = (log, entity) => {
        rows.push({
          entity,
          name: this.recordName(log),
          operation: log.operation,
          timestamp: log.timestamp,
          user_id: log.user_id,
          changes: this.changes(log),
        });
      };
      (data.main_entity || []).forEach(l => push(l, this.tableName));
      const related = data.related_logs || {};
      Object.keys(related).forEach(table => (related[table] || []).forEach(l => push(l, table)));
      rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      // Ocultar UPDATE sin cambios legibles (re-guardados / ruido de serialización).
      return rows.filter(r => r.operation !== "UPDATE" || r.changes.length > 0);
    },

    parse(v) {
      if (v == null) return {};
      if (typeof v === "string") { try { return JSON.parse(v) || {}; } catch (e) { return {}; } }
      return v;
    },

    recordName(log) {
      const nv = this.parse(log.new_values);
      const ov = this.parse(log.old_values);
      return nv.name || ov.name || "";
    },

    changes(log) {
      const oldV = this.parse(log.old_values);
      const newV = this.parse(log.new_values);
      let fields = this.parse(log.changed_fields);
      if (!Array.isArray(fields)) fields = [];
      fields = [...new Set(fields.map(f => String(f).split(",")[0]).filter(f => f && f !== "-"))];
      const ignore = ["write_date", "updated_at", "created_date", "id"];
      const isPrimitive = (v) => v == null || typeof v !== "object";
      return fields
        .filter(f => !ignore.includes(f))
        .map(f => ({ field: f, old: oldV[f], new: newV[f] }))
        .filter(c => isPrimitive(c.old) && isPrimitive(c.new))
        .filter(c => JSON.stringify(c.old) !== JSON.stringify(c.new));
    },

    opLabel(op) { return { CREATE: "Creado", UPDATE: "Cambio", DELETE: "Eliminado", DELETE_ALL: "Eliminado" }[op] || op; },
    opVariant(op) { return { CREATE: "success", UPDATE: "info", DELETE: "danger", DELETE_ALL: "danger" }[op] || "secondary"; },
    entityLabel(entity) {
      return {
        purchase_order: "Orden de compra",
        purchase_order_items: "Producto de la orden",
        product: "Producto",
        product_prices: "Precio",
        product_recipe: "Receta (insumo)",
      }[entity] || entity;
    },
    userName(userId) {
      if (userId == null) return "Sistema";
      return this.usersById[userId] || ("Usuario #" + userId);
    },
    fieldLabel(field) {
      const map = {
        // Orden de compra
        status: "Estado", name: "Nombre", provider_id: "Proveedor",
        amount_untaxed: "Subtotal", amount_tax: "Impuestos", amount_total: "Total",
        date_planned: "Fecha planeada", date_order: "Fecha de orden", notes: "Notas",
        product_quantity: "Cantidad", product_qty: "Cantidad", price_unit: "Precio unitario",
        discount: "Descuento", qty_received: "Cantidad recibida", qty_done: "Cantidad hecha",
        subtotal: "Subtotal", supply_id: "Insumo",
        // Producto
        ndescription: "Nombre", short_name: "Nombre corto", group_id: "Grupo",
        subgroup_id: "Subgrupo", clave: "Clave", barcode: "Código de barras",
        prep_area: "Área de preparación", waiter_commision: "Comisión mesero",
        meal_type_id: "Tipo de comida", image: "Imagen",
        // Precio
        price: "Precio", price_wo_tax: "Precio sin impuesto", tax: "Impuesto",
        tax_excempt: "Exento de impuesto", billable: "Facturable",
        area_restaurant_id: "Área", sucursal_id: "Sucursal", follow_price: "Sigue precio",
        // Receta
        qty_portions: "Porciones",
      };
      return map[field] || field;
    },
    valueLabel(field, value) {
      if (value == null || value === "") return "—";
      if (field === "status") {
        const s = { draft: "Borrador", sent: "Enviada", confirmed: "Confirmada", purchase: "Comprada", assigned: "Asignada", done: "Terminada", cancel: "Cancelada", 0: "Inactivo", 1: "Activo" };
        return s[value] != null ? s[value] : value;
      }
      if (typeof value === "boolean") return value ? "Sí" : "No";
      return value;
    },
    formatDate(ts) {
      if (!ts) return "";
      // El backend guarda el timestamp en UTC ("YYYY-MM-DD HH:MM:SS", sin zona). Lo interpretamos
      // como UTC y lo mostramos en la hora local del navegador con la zona visible.
      let iso = String(ts).trim().replace(" ", "T");
      if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso)) iso += "Z";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString(undefined, {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short",
      });
    },
  },
};
</script>
